import { yupResolver } from "@hookform/resolvers/yup";
import { ApiError, useAppStatus } from "@wexinc/wex-common-web";
import { Button, CheckIcon, FieldValidation, Form, Heading, TextField, WexLogo } from "@wexinc/wex-ui";
import { useEffect, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useSearchParams } from "react-router-dom";
import { useCopSecurityService } from "services/security";
import * as yup from "yup";
import BlobSVG from "./BlobSVG";
import mainImage from "./MainImage.png";

interface PasswordResetRequestSchema {
  email: string;
}

const schema = yup.object<PasswordResetRequestSchema>({
  email: yup.string().email("Must be a valid email.").trim().required("Email is required."),
});

const PasswordResetRequest = () => {
  const [isSent, setIsSent] = useState(false);
  const copSecurityService = useCopSecurityService();
  const [searchParams] = useSearchParams();
  const appStatus = useAppStatus();
  const { showBoundary } = useErrorBoundary();
  const [errorMessage, setErrorMessage] = useState<string>();

  async function handleSubmit({ email }: { email: string }) {
    try {
      await copSecurityService.resetPasswordRequest(email);
      setIsSent(true);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.code === 400) {
          setErrorMessage(error.message);
        } else {
          showBoundary(error);
        }
      } else {
        showBoundary(Error("Unexpeced error."));
      }
    }
  }

  // Annoying to have to do, but in the authorized area of the app, securityService takes care of this.
  useEffect(() => appStatus.setAppStatusAction("reset"), []);

  return (
    <>
      <div className="flex h-full items-center mx-auto">
        <div className="flex items-center mx-auto">
          <section className="ml-[20px]">
            <WexLogo
              height="40px"
              width="124px"
            />
            <div className="pb-10 pt-2 text-neutral-dark-60 text-lg-plus font-semibold">Customer Operations Portal</div>

            {!isSent && (
              <>
                <Heading
                  size="small"
                  className="mb-4 text-neutral-dark-60"
                >
                  Enter your email and we&apos;ll send you a link to reset your password.
                </Heading>
                {errorMessage && (
                  <FieldValidation
                    className="text-md text-center pb-2"
                    testId="authentication-error"
                    error={errorMessage}
                  />
                )}
                <Form
                  className="relative"
                  onSubmit={handleSubmit}
                  options={{
                    resolver: yupResolver(schema),
                    defaultValues: { email: searchParams.get("email") },
                  }}
                >
                  <>
                    <TextField
                      name="email"
                      label="Email"
                      containerClassName="mb-[45px]"
                    />

                    <Button
                      id="send-link"
                      className="w-full !h-14 mt-10"
                      testId="login-button"
                      type="submit"
                    >
                      Send link
                    </Button>
                  </>
                </Form>
              </>
            )}
            {isSent && (
              <>
                <div className="flex items-center">
                  <CheckIcon className="mr-2 text-utility-success-50 fill-current" />
                  <Heading size="medium">Request Received</Heading>
                </div>
                <div className="ml-8">
                  If the given username was found an email with reset instructions has been sent to the email address on
                  file
                </div>
              </>
            )}
          </section>
          <section className="relative ml-[110px]">
            <img
              src={mainImage}
              alt="Illustration"
              className="relative z-10"
            />
          </section>
        </div>
      </div>

      <BlobSVG
        width="596"
        height="297"
        viewBox="0 0 596 297"
        className="absolute bottom-0 right-0"
        opacity="0.5"
      />

      <BlobSVG
        width="565"
        height="285"
        viewBox="0 0 565 285"
        className="absolute bottom-0 right-0"
        opacity="1"
      />
    </>
  );
};

export default PasswordResetRequest;
